import { ComponentStatus } from '@christies/react/typings'
import { LotTileProps } from '@christies/react/dist/lib/components/Tiles/LotTile/LotTile.types'
import useSWR from 'swr'
import { Utils } from '@christies/react/.'
import { useMockData } from '../../useMockData'
import { dataFetcher as fetcher, Endpoint } from '../../dataFetcher'
import { useRelatedContentData } from '../../useRelatedContentData'
import { useTranslation } from 'react-i18next'
import { LotsResponse, ParsedLot, PrivateItemResponse, RelatedLotsKey, Response } from './types'
import { sanitizeUrl } from '../../utils'

const dataFetcher = async (endpoints: Endpoint[]): Promise<Response[]> => {
  const results = await Promise.allSettled(endpoints.map((endpoint) => fetcher<Response>(endpoint)))
  const data = results
    .filter((result): result is PromiseFulfilledResult<Response> => result.status === 'fulfilled')
    .map((result) => result.value)

  return data
}

const urlDecoder = ({
  url,
  objectIdOnline,
  title,
  objectIdDotCom,
  id,
  lotNumber,
}: {
  url: string
  objectIdDotCom?: string
  objectIdOnline?: string
  title?: string
  id?: string
  lotNumber?: string
}): string => {
  const decoder: Record<string, string | undefined> = {
    '{objectIdOnline}': objectIdOnline,
    '{title}': title,
    '{objectIdDotCom}': objectIdDotCom,
    '{id}': id,
    '{lotNumber}': lotNumber,
  }

  return url
    ?.replace(/{objectIdOnline}|{title}|{objectIdDotCom}|{id}|{lotNumber}/gi, (match) => decoder[match] as string)
    ?.toLowerCase()
}

export const useRelatedLotsData = (
  key: RelatedLotsKey,
  mockPromise: Promise<{ default: LotTileProps[] }>
): { status: ComponentStatus; data?: LotTileProps[] } => {
  const { storyAPIEndpoint, lotAPIEndpoint, psItemEndpoint, lotsOnlineUrlFormat, lotsLiveUrlFormat, psItemUrlFormat } =
    key
  const { t } = useTranslation()
  const { data: mockData, status: mockStatus } = useMockData<LotTileProps[]>(mockPromise)

  const { data: relatedContentData, status: relatedContentStatus } = useRelatedContentData(
    mockStatus === 'error' ? storyAPIEndpoint : null
  )

  const relatedLots = relatedContentData?.relatedContent.find(
    (content) => content?.type?.toLowerCase() === 'lots-privateitems'
  )?.values

  const endpoints: Endpoint[] = []

  if (relatedLots?.length) {
    const getLotsIds = (type: 'lots' | 'privateitems'): string =>
      relatedLots
        .filter((lot) => lot?.type?.toLowerCase() === type)
        .map((lot) => lot.id)
        .join('|')

    const lotIds = getLotsIds('lots')
    const psItems = getLotsIds('privateitems')

    if (lotIds) {
      endpoints.push({
        ...lotAPIEndpoint,
        parameters: {
          ...lotAPIEndpoint?.parameters,
          ids: lotIds,
        },
      })
    }
    if (psItems) {
      endpoints.push({
        ...psItemEndpoint,
        parameters: {
          ...psItemEndpoint?.parameters,
          ids: psItems,
        },
      })
    }
  }

  const { data, isLoading, error } = useSWR(
    relatedContentStatus === 'ready' && endpoints.length ? endpoints : null,
    dataFetcher
  )

  if (mockStatus !== 'error') {
    return { data: mockData, status: mockStatus }
  }

  const parsedData: ParsedLot[] | undefined = data
    ?.filter((data): data is LotsResponse | PrivateItemResponse => !!data)
    ?.flatMap((response) => {
      if (Utils.isType<LotsResponse>(response, 'lots')) {
        return response?.lots?.map((lot) => {
          const liveAuctionLink = urlDecoder({
            url: lotsLiveUrlFormat,
            title: sanitizeUrl(lot.titles.primary),
            objectIdDotCom: String(lot.objectIdDotCom),
          })

          const onlineAuctionLink = urlDecoder({
            url: lotsOnlineUrlFormat,
            objectIdOnline: String(lot.objectIdOnline),
            lotNumber: lot.lotNumber,
          })

          const href = lot.type === 'Live' ? liveAuctionLink : onlineAuctionLink

          return {
            id: lot.id,
            image: { src: lot.media[0]?.small?.href, alt: lot.media[0]?.alt },
            primaryTitle: lot.titles?.primary,
            secondaryTitle: lot.titles?.secondary,
            href,
            cta: {
              href,
              text: t('feature-discovery-magazines-relatedlots-viewlot-txt', { defaultValue: 'View lot' }),
            },
          }
        })
      } else {
        return response?.privateItems?.map((lot) => {
          const href = urlDecoder({
            url: psItemUrlFormat,
            title: sanitizeUrl(lot.title),
            id: lot.id,
          })

          return {
            id: lot.id,
            image: { src: lot.media[0]?.small?.href, alt: lot.media[0]?.alt },
            primaryTitle: lot.title,
            href,
            cta: {
              href,
              text: t('feature-discovery-magazines-relatedlots-viewlot-txt', { defaultValue: 'View lot' }),
            },
          }
        })
      }
    })

  const sortedData = relatedLots
    ?.map((lot) => lot.id)
    ?.reduce((acc, id) => {
      const lot = (parsedData || []).find((lot) => lot?.id === id)
      return lot ? [...acc, lot] : [...acc]
    }, [] as ParsedLot[])

  const getComponentStatus = (): ComponentStatus => {
    if (isLoading || relatedContentStatus === 'initial') {
      return 'initial'
    } else if (error || (!isLoading && !sortedData?.length)) {
      return 'error'
    }
    return 'ready'
  }

  return { data: sortedData, status: getComponentStatus() }
}
